import React from "react";
import BaseModal from "../BaseModal";
import { mdiInformationOutline } from "@mdi/js";
import "./EmailFiringInfoModal.css";

// Reference table of when each email is sent. Kept aligned with the backend
// EMAIL_TYPES registry (backend-R1/src/services/email.service.js). Two variants:
//   - 'editable'  → Mode 3 owner-editable templates (school can customise copy)
//   - 'automated' → Mode 1/2 system-controlled emails (preview only)
const EDITABLE_ROWS = [
    {
        name: "Re-enrolment Notification",
        fired: "When you roll a term over and click “Send Rollover Notifications”. Goes to returning students with an unconfirmed enrolment.",
        asks: "Confirm link (no payment)",
    },
    {
        name: "Payment Reminder (whole term, multi-student)",
        fired: "When you click “Send Enrolment Reminders” on a term. Goes to ALL confirmed-but-unpaid students in that term at once, so you can chase a whole class group in one go.",
        asks: "Payment",
    },
    {
        name: "Payment Request (single student)",
        fired: "When you click “Send email” on one student’s payment page, whether you just enrolled them or you’re sending a follow-up reminder. Same wording as the multi-student reminder, just sent to one person.",
        asks: "Payment",
    },
    {
        name: "Booking Payment Reminder",
        fired: "When a private-lesson booking is confirmed and after-lesson payment is due.",
        asks: "Payment",
    },
    {
        name: "Payment Received Confirmation",
        fired: "When you mark a payment as received outside the platform (e.g. bank transfer, cash, EFTPOS). Not sent for card payments. Those get a card receipt automatically.",
        asks: "Nothing (receipt)",
    },
];

const AUTOMATED_ROWS = [
    {
        name: "Dance Localé Account Invite",
        fired: "When you invite an existing student to create their Dance Localé account.",
    },
    {
        name: "Event Cancellation",
        fired: "When you cancel a ticketed event. You supply the reason.",
    },
    {
        name: "Instructor Booking Notification",
        fired: "Automatically when a student books a lesson with you.",
    },
    {
        name: "Booking Rejection",
        fired: "Automatically when an instructor declines a booking request.",
    },
    {
        name: "Booking Cancellation (Instructor)",
        fired: "Automatically when a student cancels a booking.",
    },
    {
        name: "Booking Cancellation (Student)",
        fired: "Automatically when an instructor cancels a booking.",
    },
    {
        name: "Enquiry Message",
        fired: "Automatically when a new enquiry is submitted through your public page.",
    },
    {
        name: "Trial Purchase Confirmation",
        fired: "Automatically to the buyer after a trial deal purchase succeeds.",
    },
    {
        name: "Event Ticket Confirmation",
        fired: "Automatically to the buyer after an event ticket purchase succeeds (also the service-fee tax invoice).",
    },
    {
        name: "Class Enrolment Receipt (Card Paid)",
        fired: "Automatically after a card payment for a class enrolment succeeds.",
    },
    {
        name: "Private Lesson Booking Receipt (Card Paid)",
        fired: "Automatically after a card payment for a private-lesson booking succeeds.",
    },
    {
        name: "Private Lesson Booking Confirmation (Paid By Credit)",
        fired: "Automatically when account credit fully covers a private-lesson booking.",
    },
];

const VARIANTS = {
    editable: {
        title: "When editable emails are sent",
        intro: "You can customise the copy of these emails in Settings. Each one is sent only when you take the action described below. The system never sends them on its own.",
        showAsks: true,
        rows: EDITABLE_ROWS,
    },
    automated: {
        title: "When automated emails are sent",
        intro: "These emails are sent automatically by the system in response to the events below. You can preview them but cannot edit the copy.",
        showAsks: false,
        rows: AUTOMATED_ROWS,
    },
};

const EmailFiringInfoModal = ({ isOpen, onClose, variant = "editable" }) => {
    const cfg = VARIANTS[variant] || VARIANTS.editable;

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={cfg.title}
            icon={mdiInformationOutline}
            maxWidth={680}
            footer={
                <button className="base-modal-save-btn" onClick={onClose}>
                    Got it
                </button>
            }
        >
            <div className="email-firing-info">
                <p className="email-firing-info-intro">{cfg.intro}</p>
                <table className="email-firing-info-table">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>When it's sent</th>
                            {cfg.showAsks && <th>Asks for</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {cfg.rows.map((row) => (
                            <tr key={row.name}>
                                <td className="email-firing-info-name">{row.name}</td>
                                <td>{row.fired}</td>
                                {cfg.showAsks && <td>{row.asks}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </BaseModal>
    );
};

export default EmailFiringInfoModal;
